import React from 'react';
import './PlatformRow.css';
import { Session } from '@supabase/supabase-js';
import { PlatformIcon } from './PlatformIcon';
// import { supabase } from '../lib/supabase';

// Define the Platform interface based on your platforms.ts
interface Platform {
  name: string;
  icon: string;
  // add other properties if they exist in your platforms config
}

interface PlatformRowProps {
  platformKey: string;
  platform: Platform;
  session: Session | null;
  isEnabled: boolean;
  isConnected: boolean;
  isConnecting: boolean;
  isDisconnecting: boolean;
  onToggleEnabled: () => void;
  onConnect: () => void;
  onDisconnect: () => void;
  postingMethod: 'intent' | 'oauth';
  onSetPostingMethod: (method: 'intent' | 'oauth') => void;
  customConnectComponent?: React.ReactNode;
}

export const PlatformRow: React.FC<PlatformRowProps> = ({
  platformKey,
  platform,
  session,
  isEnabled,
  isConnected,
  isConnecting,
  isDisconnecting,
  onToggleEnabled,
  onConnect,
  onDisconnect,
  postingMethod,
  onSetPostingMethod,
  customConnectComponent,
}) => {
  const [shake, setShake] = React.useState(false);
  React.useEffect(() => {
    // Lightweight visibility into state that drives labels/toggle
    // eslint-disable-next-line no-console
    console.debug('[PlatformRow] state', { platformKey, isEnabled, isConnected, postingMethod });
  }, [platformKey, isEnabled, isConnected, postingMethod]);

  // Helper: shake Connect OAuth button if disabled toggle is clicked
  const handleDisabledToggleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  return (
    <div className={`platform-row ${!isEnabled ? 'disabled' : ''} ${!session ? 'logged-out' : ''}`}>
      <div className="platform-identity">
        <label className="checkbox-container">
          <input
            type="checkbox"
            checked={isEnabled}
            onChange={onToggleEnabled}
          />
          <span className="checkmark"></span>
        <PlatformIcon platformKey={platformKey} icon={platform.icon} size={24} className="platform-icon" />
          <span className="platform-name">{platform.name}</span>
        </label>
      </div>

      <div className="platform-connection">
        {!session ? null : (
          <>
            <span className={`method-label ${postingMethod === 'intent' ? 'active' : ''}`}>Intent</span>
            <label
              className={`switch${shake ? ' shake' : ''}`}
              onClick={(!isConnected && isEnabled) ? handleDisabledToggleClick : undefined}
              style={{ cursor: (!isConnected && isEnabled) ? 'pointer' : undefined }}
            >
              <input
                type="checkbox"
                checked={postingMethod === 'oauth'}
                onChange={() => onSetPostingMethod(postingMethod === 'oauth' ? 'intent' : 'oauth')}
                disabled={!isConnected || !isEnabled}
              />
              <span className="slider round"></span>
            </label>
            {isConnected && (
              <span className={`method-label ${postingMethod === 'oauth' ? 'active' : ''}`}>
                {isDisconnecting ? 'Deleting...' : 'OAuth'}
              </span>
            )}
            <div className={`connection-action${shake ? ' shake' : ''}`}>
              {isConnected ? (
                !isDisconnecting && (
                  <button
                    onClick={onDisconnect}
                    className="disconnect-button"
                    title="Disconnect"
                  >
                    <svg className="warn" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                  </button>
                )
              ) : customConnectComponent ? (
                customConnectComponent
              ) : (
                <button
                  type="button"
                  onClick={onConnect}
                  className="connect-link"
                  style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  {isConnecting ? 'Connecting...' : 'Connect'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}; 
